import useAxiosInterceptor from 'src/services/interceptor';
import { dashboardApiCall, SIGNING_PIN_HEADER } from '../constants';

class DashboardServices {
  private axiosInstance;

  constructor() {
    // eslint-disable-next-line react-hooks/rules-of-hooks -- factory, not a React hook (matches npcLogService)
    this.axiosInstance = useAxiosInterceptor();
  }

  getDashboardPatientList = (payload: any) => {
    return this.axiosInstance.get(`${dashboardApiCall.patientv2List}`, {
      params: {
        ...payload,
        searchString: payload?.searchString,
        pageSize: 100,
        forDashboard: true,
      },
    });
  };

  getTodayPatientList = (payload: any) => {
    return this.axiosInstance.get(`${dashboardApiCall.todayPatientList}`, {
      params: payload,
    });
  };

  getDashboardOverView = (payload: any) => {
    return this.axiosInstance.get(`${dashboardApiCall.taskOverview}`, { params: payload });
  };
  getDashboardTherapyOverView = (payload: any) => {
    return this.axiosInstance.get(`${dashboardApiCall.therapySession}`, { params: payload });
  };
  getDashboardDocumentationOverView = (payload: any) => {
    return this.axiosInstance.get(`${dashboardApiCall.documentationTask}`, { params: payload });
  };

  getTimeCheckIn = (payload: any) => {
    const { providerId, ...restPayload } = payload;
    return this.axiosInstance.get(`${dashboardApiCall.timeCheckIn}/${providerId}`, {
      params: restPayload,
    });
  };

  postTimeCheckout = (payload: any) => {
    const { providerId, isLogout, clockInBody, idleTimeExpired, ...restPayload } = payload;
    return this.axiosInstance.put(`${dashboardApiCall.timeCheckout}/${providerId}`, clockInBody, {
      params: restPayload,
    });
  };

  getDashboardDocument = (payload: any) => {
    return this.axiosInstance.post(dashboardApiCall.dashboardDocuments, payload?.param, {
      params: payload?.params,
    });
  };

  getMyDashboardDocument = (payload: any) => {
    return this.axiosInstance.post(dashboardApiCall.myDashboardDocuments, payload?.param, {
      params: payload?.params,
    });
  };
  /** My Signature Pending as a server-rendered PDF (URL in the usual envelope) or CSV (blob). */
  exportMyDashboardDocuments = (payload: any, format: 'pdf' | 'csv') => {
    return this.axiosInstance.post(dashboardApiCall.myDashboardDocuments, payload?.param, {
      params: { format },
      // blob for both: a PDF may arrive as raw bytes or as a JSON envelope with a URL.
      responseType: 'blob',
    });
  };
  getDashboardCsvDocument = (payload: any) => {
    return this.axiosInstance.post(dashboardApiCall.dashboardDocuments, payload?.param, {
      params: payload?.params,
    });
  };

  /**
   * THIN-8323: Docs Due as a printable PDF or a server-written CSV.
   *
   * Sends the same filter object the table was drawn with; the server covers the
   * whole filtered list rather than the page on screen. A CSV comes back as a file
   * body, so it needs `blob`; a PDF comes back as a URL in the usual envelope.
   */
  exportDashboardDocuments = (payload: any, format: 'pdf' | 'csv') => {
    return this.axiosInstance.post(dashboardApiCall.dashboardDocumentsPrint, payload?.param, {
      params: { ...payload?.params, format },
      responseType: format === 'csv' ? 'blob' : 'json',
    });
  };

  /**
   * THIN-8352: start an async Docs Due PDF export. Returns immediately with a RUNNING
   * job (jobId); the render runs off-thread so it is not bound by the gateway's 60s
   * timeout. Poll {@link getDocsDueExportJob} until the status is terminal.
   */
  startDocsDueExportAsync = (payload: any, format: 'pdf' | 'csv' = 'pdf') => {
    return this.axiosInstance.post(dashboardApiCall.dashboardDocumentsPrintAsync, payload?.param, {
      params: { ...payload?.params, format },
    });
  };

  getDocsDueExportJob = (jobId: string) => {
    return this.axiosInstance.get(`${dashboardApiCall.dashboardDocumentsPrintJobs}/${jobId}`);
  };

  getPatientDetails = (payload: any) => {
    const { patientId, ...restPayload } = payload;
    return this.axiosInstance.get(`${dashboardApiCall.patientDetails}/${patientId}/therapy-info`, {
      params: restPayload,
    });
  };
  getLatestPtoUpdate = (payload: any) => {
    return this.axiosInstance.get(`${dashboardApiCall.ptoUpdates}/${payload?.providerId}`);
  };

  getInterventionGroup = (payload: any) => {
    return this.axiosInstance.get(`${dashboardApiCall.interventionGroup}`, {
      params: payload?.params,
    });
  };

  getGameByIntervention = (payload: any) => {
    return this.axiosInstance.get(
      `${dashboardApiCall.gamesByIntervention}/${payload?.interventionId}`
    );
  };
  getAutoClockOutCall = (payload: any) => {
    return this.axiosInstance.put(`${dashboardApiCall.autoClockOut}`, null, {
      params: payload,
    });
  };

  getTherapistDailyUpdates = (payload: any) => {
    return this.axiosInstance.get(
      `${dashboardApiCall.therapistDailyUpdates}/${payload?.providerId}?date=${payload?.currentDate}`,
      {
        params: payload?.params,
      }
    );
  };

  getDashboardSupervisorEdit = () => {
    return this.axiosInstance.get(dashboardApiCall.dashboardSupervisorEdit);
  };

  updateCoSignatureStatus = (payload: any) => {
    return this.axiosInstance.put(`${dashboardApiCall.coSignatureStatus}`, null, {
      params: payload,
    });
  };

  applyTherapistBulkSign = (payload: any) => {
    // THIN-7279: BE's /provider/signature/bulk-sign-documents reads the signer's IP
    // from X-Forwarded-For and stores it on each row's signatureMetadata.signerIpAddress.
    // Without this header the IP lands as null on every bulk-signed audit row (covers
    // both therapist and supervisor signers, since the endpoint dispatches per-item).
    // Mirrors signPhysicianDocument / undoDocSignatureFlow which already do this.
    //
    // THIN-7775: the PIN arrives inside payload.params — pull it out into SIGNING_PIN_HEADER so
    // it never reaches the query string. payload.body (the document array) is untouched.
    const { pin, ...params } = payload?.params ?? {};
    return this.axiosInstance.post(dashboardApiCall.therapistBulkSign, payload?.body, {
      params,
      headers: {
        'X-Forwarded-For': localStorage.getItem('ip-address') ?? '',
        ...(pin ? { [SIGNING_PIN_HEADER]: pin } : {}),
      },
    });
  };

  getDsnSummary = (payload: any) => {
    return this.axiosInstance.get(dashboardApiCall.dsnSummary(payload?.patientId, payload?.type), {
      params: payload?.params,
    });
  };
}

const dashboardServices = new DashboardServices();

Object.freeze(dashboardServices);

export default dashboardServices;
